import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import type { CumulativeAssessmentLockView, CumulativeScopeChapterView, CumulativeSegmentView } from '@/api/lcLearning'
import { CumulativeAssessmentNotice } from '@/components/learner/cumulative/CumulativeAssessmentNotice'
import { TestCardSurface } from '@/components/learner/test/TestLayout'
import { cn } from '@/lib/utils'

/**
 * 로컬 개발 전용 누적 테스트 안내 미리보기 (`/__preview/cumulative`).
 *
 * 누적 테스트는 학습자가 진도를 밀어 활성화해야 볼 수 있고 상태 조합도 많아, 운영 데이터 없이 케이스를 훑기
 * 어렵다. 이 페이지는 실제 `CumulativeAssessmentNotice`를 실제 프레임 그대로 렌더하고 lock view 만 갈아끼운다.
 * App.tsx 에서 `import.meta.env.DEV` 로 감싸 배포 번들에는 라우트가 들어가지 않는다.
 */

const segment = (
  status: CumulativeSegmentView['status'],
  overrides: Partial<CumulativeSegmentView> = {},
): CumulativeSegmentView => ({
  status,
  latestRunId: null,
  roundNo: null,
  canStartOrResume: status === 'AVAILABLE' || status === 'IN_PROGRESS',
  ...overrides,
})

/** 레슨 테스트는 교사가 출력물로 진행한다 — 학습자 화면에서는 시작할 수 없다. */
const lessonSegment = (status: CumulativeSegmentView['status']): CumulativeSegmentView =>
  segment(status, { canStartOrResume: false })

const SAME_COURSE: CumulativeScopeChapterView[] = [
  { chapterOrdinal: 1, chapterNumberInCourse: 1, chapterTitle: '하나에서 아홉까지', courseTitle: 'CORE-A1' },
  { chapterOrdinal: 2, chapterNumberInCourse: 2, chapterTitle: '0과 더하기 빼기', courseTitle: 'CORE-A1' },
  { chapterOrdinal: 3, chapterNumberInCourse: 3, chapterTitle: '단위로 간단하게', courseTitle: 'CORE-A1' },
]

// LC-과정C 기준: 코어 C 는 18챕터(전역 1~18), 어드밴스드가 전역 19부터 시작한다.
const ACROSS_COURSES: CumulativeScopeChapterView[] = [
  { chapterOrdinal: 17, chapterNumberInCourse: 17, chapterTitle: '비와 비율', courseTitle: 'CORE-C' },
  { chapterOrdinal: 18, chapterNumberInCourse: 18, chapterTitle: '0과 더하기 빼기', courseTitle: 'CORE-C' },
  { chapterOrdinal: 19, chapterNumberInCourse: 1, chapterTitle: '단위로 간단하게', courseTitle: 'ADVANCED' },
]

const lock = (overrides: Partial<CumulativeAssessmentLockView> = {}): CumulativeAssessmentLockView => ({
  assessmentId: 77,
  status: 'ACTIVE',
  scope: { startChapterOrdinal: 1, endChapterOrdinal: 3, chapters: SAME_COURSE },
  chapterComprehensive: lessonSegment('AVAILABLE'),
  mathHead: segment('AVAILABLE'),
  ...overrides,
})

interface PreviewCase {
  id: string
  label: string
  note: string
  assessment: CumulativeAssessmentLockView
}

const CASES: PreviewCase[] = [
  {
    id: 'available',
    label: '수학머리 시작 전',
    note: '기본 진입 상태. 레슨 테스트는 교사가 출력물로 진행하므로 화면에 두지 않는다.',
    assessment: lock(),
  },
  {
    id: 'in-progress',
    label: '수학머리 진행 중',
    note: 'BIGGO run 이 열려 있어 [이어서 풀기]로 바뀐다.',
    assessment: lock({ mathHead: segment('IN_PROGRESS', { latestRunId: 88, roundNo: 1 }) }),
  },
  {
    id: 'math-done',
    label: '수학머리 완료 · 레슨 테스트 남음',
    note: '학습자가 할 일은 끝났지만 교사의 레슨 테스트가 남아 있다. 면제 처리도 같은 화면이 된다.',
    assessment: lock({ mathHead: segment('COMPLETED', { latestRunId: 88, roundNo: 1, canStartOrResume: false }) }),
  },
  {
    id: 'both-done',
    label: '둘 다 완료 · 교사 확인 대기',
    note: '두 세그먼트가 모두 종결됐고 교사의 [누적 테스트 완료]만 남았다.',
    assessment: lock({
      chapterComprehensive: lessonSegment('COMPLETED'),
      mathHead: segment('COMPLETED', { latestRunId: 88, roundNo: 1, canStartOrResume: false }),
      awaitingTeacherResolution: true,
    }),
  },
  {
    id: 'across-courses',
    label: '시험범위가 과정 경계를 넘음',
    note: '과정이 바뀌면 과정 이름을 다시 보여주고 챕터 번호도 1부터 다시 센다. 범위는 언제나 3챕터 고정이다.',
    assessment: lock({
      scope: { startChapterOrdinal: 17, endChapterOrdinal: 19, chapters: ACROSS_COURSES },
    }),
  },
]

export default function CumulativeNoticePreview() {
  const { t, i18n } = useTranslation('learner')
  const [params, setParams] = useSearchParams()
  const active = CASES.find((c) => c.id === params.get('case')) ?? CASES[0]

  return (
    <div className="min-h-dvh bg-lc-bg-secondary">
      <div className="sticky top-0 z-10 border-b border-lc-v3-gray-300 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-[1040px] flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-lc-warning-100 px-3 py-1 text-lc-v3-label-sm font-bold text-lc-text-warning">
              DEV PREVIEW
            </span>
            <span className="text-lc-v3-body-2 font-semibold text-lc-text-secondary">
              누적 테스트 안내 — 상태별 미리보기
            </span>
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language === 'ko' ? 'en' : 'ko')}
              className="ml-auto rounded-lg border border-lc-v3-gray-300 px-3 py-1.5 text-lc-v3-label-md font-semibold text-lc-text-secondary"
            >
              {i18n.language === 'ko' ? 'EN 으로 보기' : '한국어로 보기'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {CASES.map((preview) => (
              <button
                key={preview.id}
                type="button"
                onClick={() => setParams({ case: preview.id })}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-lc-v3-label-md font-semibold',
                  preview.id === active.id
                    ? 'bg-lc-key-500 text-lc-text-on-key'
                    : 'bg-lc-v3-gray-100 text-lc-text-secondary',
                )}
              >
                {preview.label}
              </button>
            ))}
          </div>
          <p className="text-lc-v3-caption-1 text-lc-text-tertiary">{active.note}</p>
        </div>
      </div>

      {/* TodayClass 의 누적 테스트 분기와 같은 프레임 — 헤더/탭바가 숨겨진 전체화면 카드. */}
      <section className="flex min-h-full bg-lc-bg-secondary p-4 md:p-10">
        <TestCardSurface>
          <CumulativeAssessmentNotice
            key={active.id}
            assessment={active.assessment}
            onMathHead={() => console.info('[preview] 수학머리 테스트로 이동 — 미리보기에서는 동작하지 않습니다.')}
            t={t}
          />
        </TestCardSurface>
      </section>
    </div>
  )
}
